<script lang="ts" type="metadata">
  import { onMount } from "svelte";
  import { locale } from "svelte-i18n";
  import type { Page } from "@sveltejs/kit";
  import type { Readable } from "svelte/store";
  import { page as pageStore } from "$app/stores";
  import type { MetaModel } from "./Meta.models";
  import { getRegionsData } from "$lib/data/regionSelector.data";
  import {
    getAbsoluteUrl,
    buildURL,
    normaliseURL,
    stripBasePathAndLang,
    removeTrailingSlash,
  } from "$lib/utils/url";

  const regionsData = getRegionsData();
  const rowCountryCodes = ["GB", "US", "CA"];
  const euCountryCodes = ["FR", "IT", "ES"];
  const deCountryCode = ["DE"];

  let filteredRegions = regionsData.filter(
    region => region.regionCode === "uk",
  );
  const regionCode = regionsData.filter(region => region.regionCode);
  export let metaData: MetaModel;
  let page: Readable<Page> = pageStore;

  $: ({
    title,
    description,
    keywords,
    twitterCard,
    twitterImg,
    ogImg,
    ogType,
    canonicalUrl,
    product,
  } = metaData);

  $: isLandingPage = $page?.route.id === "/[[lang=lang]]";

  function getLang(locale: string): string {
    return locale && locale.split("-")[0];
  }

  onMount(() => {
    // delete these tags on unmount to fix client-side issue where
    // svelte:head just keeps appending and doesn't hydrate existing meta tags and links
    return () => {
      document.head
        .querySelectorAll(
          "meta[data-svelte], link[data-svelte], link[rel=alternate]",
        )
        .forEach(f => f.remove());
    };
  });
</script>

<svelte:head>
  {#if title}
    <title>{title}</title>
    <meta name="twitter:title" content={title} />
    <meta property="og:title" content={title} />
  {/if}

  {#if description}
    <meta name="description" content={description} />
    <meta property="og:description" content={description} />
    <meta name="twitter:description" content={description} />
  {/if}

  {#if ogImg}
    <meta property="og:image" content={ogImg.url} />
    <meta property="og:image:alt" content={ogImg.alt} />
    <meta property="og:image:width" content={ogImg.width.toString()} />
    <meta property="og:image:height" content={ogImg.height.toString()} />
    <meta name="twitter:image" content={twitterImg} />
    <meta name="twitter:image:alt" content={ogImg.alt} />
  {/if}

  {#if keywords}
    <meta name="keywords" content={keywords.join(",")} />
  {/if}
  <meta name="author" content="Belstaff" />
  <meta http-equiv="content-language" content={getLang($locale)} />
  <meta name="twitter:card" content={twitterCard || "summary_large_image"} />
  <meta name="twitter:site" content="@Belstaff" />
  <meta name="twitter:creator" content="@Belstaff" />
  <meta property="og:type" content={ogType || "website"} />
  <meta
    property="og:url"
    content="https://{normaliseURL(canonicalUrl || getAbsoluteUrl($page))}"
  />
  <meta property="og:locale" content={getLang($locale)} />
  <meta property="og:site_name" content="Belstaff" />

  {#if product}
    <meta property="og:price:amount" content={product.price} />
    <meta property="og:price:currency" content={product.currency} />
  {/if}

  <link
    rel="canonical"
    href={"https://" +
      normaliseURL(
        isLandingPage
          ? `${getAbsoluteUrl($page) + "/"}`
          : removeTrailingSlash(getAbsoluteUrl($page)),
      )}
  />

  {#each filteredRegions as r}
    <link
      rel="alternate"
      hreflang="x-default"
      href={product
        ? removeTrailingSlash(
            `https://www.belstaff.com/${r.regionCode}/${product.pathOverride}`,
          )
        : removeTrailingSlash(
            `https://www.belstaff.com/${r.regionCode}/${stripBasePathAndLang(
              $page.url.pathname,
            )}`,
          )}
    />
  {/each}

  {#each regionsData as r}
    {#each r.countries.filter( c => rowCountryCodes.includes(c.countryCode), ) as c}
      {#each c.languages as lang}
        <link
          rel="alternate"
          hreflang={lang + "-" + c.countryCode}
          href={buildURL(
            c.regionCode ? c.regionCode : r.regionCode,
            lang,
            $page,
            product ? product.pathOverride : null,
            c.defaultLanguage ? c.defaultLanguage : null,
          )}
        />
      {/each}
    {/each}
    {#each r.countries.filter(c => deCountryCode.includes(c.countryCode)) as c}
      {#each c.languages.filter(l => l === c.countryCode.toLowerCase()) as lang}
        <link
          rel="alternate"
          hreflang={lang + "-" + c.countryCode}
          href={buildURL(
            c.regionCode ? c.regionCode : r.regionCode,
            lang,
            $page,
            product ? product.pathOverride : null,
            c.defaultLanguage ? c.defaultLanguage : null,
          )}
        />
      {/each}
    {/each}
    {#each r.countries.filter(c => euCountryCodes.includes(c.countryCode)) as c}
      {#each c.languages.filter(l => l === c.countryCode.toLowerCase()) as lang}
        <link
          rel="alternate"
          hreflang={lang}
          href={buildURL(
            c.regionCode ? c.regionCode : r.regionCode,
            lang,
            $page,
            product ? product.pathOverride : null,
            c.defaultLanguage ? c.defaultLanguage : null,
          )}
        />
      {/each}
    {/each}
  {/each}
</svelte:head>
